import {
  type MutableRefObject,
  type PointerEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { isRecord } from '@revelio/core/is-record';

export type PersistedOffset = { x: number; y: number };

type UsePersistedDraggableOffsetOptions = {
  storageKey: string;
  /** Re-measure and clamp when this is true (e.g. panel is visible). */
  enabled: boolean;
  topPaddingPx?: number;
  edgeMarginPx?: number;
};

type DragSession = {
  pointerId: number;
  startX: number;
  startY: number;
  origX: number;
  origY: number;
};

const DRAG_THRESHOLD_PX = 4;

const readNumberField = (
  record: Record<string, unknown>,
  key: string
): number | undefined => {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
};

const parseStoredOffset = (raw: string): PersistedOffset | null => {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return null;
    }
    const x = readNumberField(parsed, 'x');
    const y = readNumberField(parsed, 'y');
    if (x === undefined || y === undefined) {
      return null;
    }
    return { x, y };
  } catch {
    return null;
  }
};

const clampOffsetForCenteredPanel = (
  el: HTMLDivElement | null,
  x: number,
  y: number,
  topPaddingPx: number,
  edgeMarginPx: number
): PersistedOffset => {
  if (el === null || typeof window === 'undefined') {
    return { x, y };
  }
  const { width: bw, height: bh } = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const centerLeft = vw / 2 - bw / 2;
  const minX = edgeMarginPx - centerLeft;
  const maxX = vw - edgeMarginPx - bw - centerLeft;
  const minY = edgeMarginPx - topPaddingPx;
  const maxY = vh - edgeMarginPx - bh - topPaddingPx;
  const loX = Math.min(minX, maxX);
  const hiX = Math.max(minX, maxX);
  const loY = Math.min(minY, maxY);
  const hiY = Math.max(minY, maxY);
  return {
    x: Math.min(hiX, Math.max(loX, x)),
    y: Math.min(hiY, Math.max(loY, y)),
  };
};

/**
 * Drag offset for a panel that is horizontally centered in a full-width row
 * (default impersonation banner layout). Persists { x, y } to localStorage.
 */
export function usePersistedDraggableOffset({
  storageKey,
  enabled,
  topPaddingPx = 8,
  edgeMarginPx = 16,
}: UsePersistedDraggableOffsetOptions): {
  moveableRef: MutableRefObject<HTMLDivElement | null>;
  offset: PersistedOffset;
  isDragging: boolean;
  pointerHandlers: {
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: PointerEvent<HTMLDivElement>) => void;
    onPointerUp: (event: PointerEvent<HTMLDivElement>) => void;
    onPointerCancel: (event: PointerEvent<HTMLDivElement>) => void;
  };
  consumeSuppressClick: () => boolean;
} {
  const moveableRef = useRef<HTMLDivElement | null>(null);
  const [offset, setOffset] = useState<PersistedOffset>(() => {
    if (typeof window === 'undefined') {
      return { x: 0, y: 0 };
    }
    const stored = window.localStorage.getItem(storageKey);
    if (stored === null) {
      return { x: 0, y: 0 };
    }
    return parseStoredOffset(stored) ?? { x: 0, y: 0 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const didDragRef = useRef(false);
  const dragSessionRef = useRef<DragSession | null>(null);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }
    const reclamp = () => {
      setOffset((prev) =>
        clampOffsetForCenteredPanel(
          moveableRef.current,
          prev.x,
          prev.y,
          topPaddingPx,
          edgeMarginPx
        )
      );
    };
    reclamp();
    window.addEventListener('resize', reclamp);
    return () => {
      window.removeEventListener('resize', reclamp);
    };
  }, [edgeMarginPx, enabled, topPaddingPx]);

  const finishDrag = (event: PointerEvent<HTMLDivElement>) => {
    const session = dragSessionRef.current;
    if (session === null || session.pointerId !== event.pointerId) {
      return;
    }
    dragSessionRef.current = null;
    setIsDragging(false);
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // ignore
    }
    const dx = event.clientX - session.startX;
    const dy = event.clientY - session.startY;
    const finalOffset = clampOffsetForCenteredPanel(
      moveableRef.current,
      session.origX + dx,
      session.origY + dy,
      topPaddingPx,
      edgeMarginPx
    );
    setOffset(finalOffset);
    if (!didDragRef.current) {
      return;
    }
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(finalOffset));
    } catch {
      // ignore
    }
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }
    const { target } = event;
    if (!(target instanceof Element)) {
      return;
    }
    if (target.closest('button, [data-banner-no-drag]')) {
      return;
    }
    didDragRef.current = false;
    dragSessionRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origX: offset.x,
      origY: offset.y,
    };
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const session = dragSessionRef.current;
    if (session === null || session.pointerId !== event.pointerId) {
      return;
    }
    const dx = event.clientX - session.startX;
    const dy = event.clientY - session.startY;
    if (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX) {
      didDragRef.current = true;
    }
    setOffset(
      clampOffsetForCenteredPanel(
        moveableRef.current,
        session.origX + dx,
        session.origY + dy,
        topPaddingPx,
        edgeMarginPx
      )
    );
  };

  const consumeSuppressClick = () => {
    if (!didDragRef.current) {
      return false;
    }
    didDragRef.current = false;
    return true;
  };

  return {
    moveableRef,
    offset,
    isDragging,
    pointerHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: finishDrag,
      onPointerCancel: finishDrag,
    },
    consumeSuppressClick,
  };
}
